// src/ui/ProfileDetailsKit.jsx — "Profile details" in the browser: the card on
// a person's profile, the full list, and the owner's editor with the school,
// company and city searches that bring their logos. The phone's twin is
// src/components/ProfileDetails.tsx; the rules are services/profileDetails
// (generated from the phone's TypeScript), so the two read the same.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AUDIENCES, DEGREES, LANGUAGES, LINK_KEYS, MAX_BIO, MAX_ITEMS, MIN_YEAR, PRONOUNS, RELATIONSHIPS, SCHOOL_KINDS,
  cleanDetails, degreeLabel, detailsL, eduLine, eduSub, emptyDetails, fill, flagOf, introLines,
  isEmpty, linkHandle, linkUrl, maxYear, newId, placeLabel, relationshipLabel, schoolKindLabel, workLine, workSub,
} from '../services/profileDetails';
import { cachedDetails, fetchDetails, onDetailsChange, saveDetails, searchOrgs, searchPlaces } from '../services/profileDetailsApi';
import { X, Search, Pencil, Plus, Briefcase, School, Home, MapPin, Heart, Globe, Person, Languages, Lock, Group, Trash, ChevronRight, Link as LinkGlyph, Check } from './icons';

const lang = () => (typeof navigator !== 'undefined' ? (navigator.language || 'en') : 'en').slice(0, 2);
const LINK_NAME = { facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok', x: 'X', youtube: 'YouTube', linkedin: 'LinkedIn' };
const AUD_ICON = { public: Globe, friends: Group, only_me: Lock };
const ICON = { briefcase: Briefcase, school: School, home: Home, location: MapPin, heart: Heart, globe: Globe, link: LinkGlyph, lang: Languages, person: Person };

export function OrgLogo({ org, size = 40, icon = 'briefcase' }) {
  const [broken, setBroken] = useState(false);
  const I = ICON[icon] || Briefcase;
  if (org && org.logo && !broken) {
    return <span className="pd-logo" style={{ width: size, height: size }}><img src={org.logo} alt="" onError={() => setBroken(true)} /></span>;
  }
  return (
    <span className="pd-logo blank" style={{ width: size, height: size, fontSize: size * 0.42 }}>
      {org && org.name ? org.name.charAt(0).toUpperCase() : <I width={size * 0.5} height={size * 0.5} />}
    </span>
  );
}
function Badge({ icon, flag, size = 40 }) {
  const I = ICON[icon] || Person;
  return <span className="pd-logo blank" style={{ width: size, height: size, fontSize: size * 0.55 }}>{flag || <I width={size * 0.5} height={size * 0.5} />}</span>;
}
/** Who can see this line — a compact chooser beside the field. */
function AudPill({ value, onChange }) {
  const L = lang();
  return (
    <select className="pd-aud" value={value} onChange={(e) => onChange(e.target.value)} title={detailsL('who_sees', L)} aria-label={detailsL('who_sees', L)}>
      {AUDIENCES.map((a) => <option key={a} value={a}>{detailsL(a, L)}</option>)}
    </select>
  );
}
function AudIcon({ a }) {
  const I = AUD_ICON[a] || Globe;
  return <span className="pd-audicon" title={detailsL(a, lang())}><I width={14} height={14} /></span>;
}

/** The few lines under the name — Facebook's "Intro". */
export function ProfileDetailsCard({ uid, isOwner, onSeeAll, onEdit }) {
  const L = lang();
  const [d, setD] = useState(() => (cachedDetails(uid) || {}).details || null);
  useEffect(() => {
    let live = true;
    const hit = cachedDetails(uid);
    setD(hit ? hit.details : null);
    /* 🟥 your own card always shows "Add details", even when the answer did not come */
    fetchDetails(uid).then((r) => { if (!live) return; if (r) setD(r.details); else if (isOwner) setD((cur) => cur || emptyDetails()); })
      .catch(() => { if (live && isOwner) setD((cur) => cur || emptyDetails()); });
    const off = onDetailsChange((who) => { if (who === String(uid)) { const h = cachedDetails(uid); if (h && live) setD(h.details); } });
    return () => { live = false; off(); };
  }, [uid, isOwner]);
  if (!d) return null;
  const empty = isEmpty(d);
  if (empty && !isOwner) return null;
  return (
    <div className="pd-card">
      {d.bio ? <p className="pd-bio">{d.bio.value}</p> : null}
      {introLines(d, L, 5).map((l) => {
        const I = ICON[l.icon] || Person;
        return (
          <div className="pd-line" key={l.key}>
            {l.logo ? <OrgLogo org={{ logo: l.logo, name: l.text }} size={22} /> : <span className="pd-ico"><I width={17} height={17} /></span>}
            <span>{l.text}</span>
          </div>
        );
      })}
      <div className="pd-actions">
        {empty ? (
          <button type="button" className="pd-btn" onClick={onEdit}><Plus width={15} height={15} />{detailsL('add', L)}</button>
        ) : (
          <>
            <button type="button" className="pd-btn" onClick={onSeeAll}>{detailsL('see_all', L)}</button>
            {isOwner ? <button type="button" className="pd-btn" onClick={onEdit}><Pencil width={14} height={14} />{detailsL('edit', L)}</button> : null}
          </>
        )}
      </div>
    </div>
  );
}

/** All of a person's details (theirs to see), and the owner's editor. */
export function ProfileDetailsSheet({ uid, isOwner, startEditing = false, name, onClose }) {
  const L = lang();
  const [data, setData] = useState(() => (cachedDetails(uid) || {}).details || null);
  const [editing, setEditing] = useState(isOwner && startEditing);
  const [draft, setDraft] = useState(() => cleanDetails((cachedDetails(uid) || {}).details || emptyDetails()));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [stack, setStack] = useState([]);
  const push = (r) => setStack((st) => st.concat([r]));
  const pop = () => setStack((st) => st.slice(0, -1));

  useEffect(() => {
    let live = true;
    fetchDetails(uid).then((r) => { if (live && r) { setData(r.details); if (!dirty) setDraft(cleanDetails(r.details)); } }).catch(() => {});
    return () => { live = false; };
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') { if (stack.length) pop(); else tryClose(); } };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  });

  const change = (fn) => { setDraft((cur) => { const next = JSON.parse(JSON.stringify(cur)); fn(next); return next; }); setDirty(true); };
  const tryClose = () => {
    if (saving) return;
    if (editing && dirty && !window.confirm(`${detailsL('discard', L)}?`)) return;
    onClose();
  };
  /* ✅ a save always says so ("Saved ✓") and finishes — opened to edit, it closes a
     beat later; opened to look, it goes back to the details showing what was saved */
  const [savedFlash, setSavedFlash] = useState(false);
  const flashT = useRef(null);
  useEffect(() => () => { if (flashT.current) clearTimeout(flashT.current); }, []);
  const save = async () => {
    if (saving) return;
    if (!dirty) { if (startEditing) onClose(); else setEditing(false); return; }
    setSaving(true); setErr('');
    const out = await saveDetails(uid, draft);
    setSaving(false);
    if (!out) { setErr(detailsL('save_failed', L)); return; }
    setData(out); setDraft(cleanDetails(out)); setDirty(false);
    setEditing(false); setSavedFlash(true);
    if (flashT.current) clearTimeout(flashT.current);
    flashT.current = setTimeout(() => { flashT.current = null; setSavedFlash(false); if (startEditing) onClose(); }, 750);
  };

  const shown = editing ? draft : (data || emptyDetails());

  return createPortal(
    <div className="pd-back" onClick={tryClose} role="presentation">
      <div className="pd-sheet" role="dialog" aria-label={detailsL('title', L)} onClick={(e) => e.stopPropagation()}>
        <header className="pd-head">
          <button type="button" className="pd-x" onClick={tryClose} aria-label="Close"><X width={18} height={18} /></button>
          <div className="pd-ttl"><b>{editing ? detailsL('edit', L) : detailsL('title', L)}</b>{!editing && name ? <i>{name}</i> : null}</div>
          {isOwner && !editing ? <button type="button" className="pd-btn" onClick={() => { setDraft(cleanDetails(shown)); setEditing(true); setDirty(false); }}><Pencil width={14} height={14} />{detailsL('edit', L)}</button> : null}
          {editing ? <button type="button" className="pd-btn primary" disabled={saving} onClick={save}>{saving ? detailsL('saving', L) : detailsL('save', L)}</button> : null}
        </header>
        <div className="pd-body">
          {err ? <div className="pd-err">{err}</div> : null}
          {editing ? <p className="pd-note">{detailsL('optional', L)}</p> : null}
          {!data && !editing ? <p className="pd-note">…</p> : null}
          {data && !editing && isEmpty(shown) ? <p className="pd-note center">{detailsL('nothing', L)}</p> : null}

          {editing ? (
            <Sec title={detailsL('bio', L)}>
              <textarea className="pd-in" maxLength={MAX_BIO} rows={3} placeholder={detailsL('bio_ph', L)} value={draft.bio ? draft.bio.value : ''}
                onChange={(e) => change((d) => { const v = e.target.value; d.bio = v ? { value: v, aud: d.bio ? d.bio.aud : 'public' } : undefined; })} />
              <div className="pd-row-end"><small>{(draft.bio ? draft.bio.value : '').length}/{MAX_BIO}</small>{draft.bio ? <AudPill value={draft.bio.aud} onChange={(a) => change((d) => { d.bio.aud = a; })} /> : null}</div>
            </Sec>
          ) : shown.bio ? <p className="pd-bio big">{shown.bio.value}</p> : null}

          {(editing || shown.work.length > 0) && (
            <Sec title={detailsL('work', L)}>
              {shown.work.map((w) => (
                <Row key={w.id} logo={<OrgLogo org={w.org} />} title={workLine(w, L)} sub={workSub(w, L)} aud={isOwner ? w.aud : null}
                  onClick={editing ? () => push({ kind: 'work', item: JSON.parse(JSON.stringify(w)) }) : null} />
              ))}
              {editing && draft.work.length < MAX_ITEMS ? <AddRow label={detailsL('add_work', L)} onClick={() => push({ kind: 'work', item: { id: newId(), org: { name: '' }, aud: 'public' } })} /> : null}
            </Sec>
          )}

          {(editing || shown.education.length > 0) && (
            <Sec title={detailsL('education', L)}>
              {shown.education.map((e) => (
                <Row key={e.id} logo={<OrgLogo org={e.org || null} icon="school" />} title={eduLine(e, L)} sub={eduSub(e, L)} aud={isOwner ? e.aud : null}
                  onClick={editing ? () => push({ kind: 'edu', item: JSON.parse(JSON.stringify(e)) }) : null} />
              ))}
              {editing && draft.education.length < MAX_ITEMS ? <AddRow label={detailsL('add_school', L)} onClick={() => push({ kind: 'edu', item: { id: newId(), kind: 'university', aud: 'public' } })} /> : null}
            </Sec>
          )}

          {(editing || shown.places.current || shown.places.hometown) && (
            <Sec title={detailsL('places', L)}>
              {['current', 'hometown'].map((k) => {
                const p = shown.places[k];
                const label = detailsL(k === 'current' ? 'current_city' : 'hometown', L);
                if (!editing) return p ? <Row key={k} logo={<Badge icon={k === 'current' ? 'home' : 'location'} flag={flagOf(p.cc)} />} title={placeLabel(p)} sub={label} aud={isOwner ? p.aud : null} /> : null;
                return (
                  <div key={k} className="pd-field">
                    <label>{label}</label>
                    <div className="pd-pick-row">
                      <button type="button" className="pd-pick" onClick={() => push({ kind: 'search', mode: 'city', onPick: (pl) => change((d) => { d.places[k] = { ...pl, aud: d.places[k] ? d.places[k].aud : 'public' }; }) })}>
                        {p ? <><span className="pd-flag">{flagOf(p.cc)}</span>{placeLabel(p)}</> : <span className="ph"><Search width={14} height={14} />{detailsL('search_city', L)}</span>}
                      </button>
                      {p ? <AudPill value={p.aud} onChange={(a) => change((d) => { d.places[k].aud = a; })} /> : null}
                      {p ? <button type="button" className="pd-x small" onClick={() => change((d) => { delete d.places[k]; })} aria-label={detailsL('remove', L)}><X width={14} height={14} /></button> : null}
                    </div>
                  </div>
                );
              })}
            </Sec>
          )}

          {(editing || shown.basic.pronouns || shown.basic.languages) && (
            <Sec title={detailsL('basic', L)}>
              {editing ? (
                <>
                  <div className="pd-field">
                    <label>{detailsL('pronouns', L)}</label>
                    <Chips items={PRONOUNS} value={draft.basic.pronouns ? draft.basic.pronouns.value : undefined} label={(p) => detailsL(p, L)}
                      onChange={(v) => change((d) => { d.basic.pronouns = v ? { value: v, aud: d.basic.pronouns ? d.basic.pronouns.aud : 'public' } : undefined; })} />
                    {draft.basic.pronouns ? <AudPill value={draft.basic.pronouns.aud} onChange={(a) => change((d) => { d.basic.pronouns.aud = a; })} /> : null}
                  </div>
                  <div className="pd-field">
                    <label>{detailsL('languages', L)}</label>
                    <div className="pd-chips">
                      {(draft.basic.languages ? draft.basic.languages.value : []).map((l) => (
                        <button type="button" key={l} className="pd-chip" onClick={() => change((d) => { const left = d.basic.languages.value.filter((x) => x !== l); d.basic.languages = left.length ? { value: left, aud: d.basic.languages.aud } : undefined; })}>{l} <X width={12} height={12} /></button>
                      ))}
                      <button type="button" className="pd-chip add" onClick={() => push({ kind: 'lang' })}><Plus width={13} height={13} />{detailsL('add_language', L)}</button>
                    </div>
                    {draft.basic.languages ? <AudPill value={draft.basic.languages.aud} onChange={(a) => change((d) => { d.basic.languages.aud = a; })} /> : null}
                  </div>
                </>
              ) : (
                <>
                  {shown.basic.pronouns ? <Row logo={<Badge icon="person" />} title={detailsL(shown.basic.pronouns.value, L)} sub={detailsL('pronouns', L)} aud={isOwner ? shown.basic.pronouns.aud : null} /> : null}
                  {shown.basic.languages ? <Row logo={<Badge icon="lang" />} title={shown.basic.languages.value.join(', ')} sub={detailsL('languages', L)} aud={isOwner ? shown.basic.languages.aud : null} /> : null}
                </>
              )}
            </Sec>
          )}

          {(editing || shown.relationship) && (
            <Sec title={detailsL('relationship', L)}>
              {editing ? (
                <div className="pd-field">
                  <Chips items={RELATIONSHIPS} value={draft.relationship ? draft.relationship.value : undefined} label={(r) => relationshipLabel(r, L)}
                    onChange={(v) => change((d) => { d.relationship = v ? { value: v, aud: d.relationship ? d.relationship.aud : 'friends' } : undefined; })} />
                  {draft.relationship ? <AudPill value={draft.relationship.aud} onChange={(a) => change((d) => { d.relationship.aud = a; })} /> : null}
                </div>
              ) : shown.relationship ? <Row logo={<Badge icon="heart" />} title={relationshipLabel(shown.relationship.value, L)} aud={isOwner ? shown.relationship.aud : null} /> : null}
            </Sec>
          )}

          {(editing || shown.contact.website || LINK_KEYS.some((k) => shown.contact.links[k])) && (
            <Sec title={detailsL('contact', L)}>
              {editing ? (
                <>
                  <LinkEdit label={detailsL('website', L)} ph="example.com" value={draft.contact.website ? draft.contact.website.value : ''} aud={draft.contact.website ? draft.contact.website.aud : 'public'}
                    onChange={(v) => change((d) => { d.contact.website = v ? { value: v, aud: d.contact.website ? d.contact.website.aud : 'public' } : undefined; })}
                    onAud={(a) => change((d) => { if (d.contact.website) d.contact.website.aud = a; })} />
                  {LINK_KEYS.map((k) => (
                    <LinkEdit key={k} label={LINK_NAME[k]} ph="@username" value={draft.contact.links[k] ? draft.contact.links[k].value : ''} aud={draft.contact.links[k] ? draft.contact.links[k].aud : 'public'}
                      onChange={(v) => change((d) => { if (v) d.contact.links[k] = { value: v, aud: d.contact.links[k] ? d.contact.links[k].aud : 'public' }; else delete d.contact.links[k]; })}
                      onAud={(a) => change((d) => { if (d.contact.links[k]) d.contact.links[k].aud = a; })} />
                  ))}
                </>
              ) : (
                <>
                  {shown.contact.website ? <Row logo={<Badge icon="globe" />} title={shown.contact.website.value.replace(/^https?:\/\/(www\.)?/, '')} sub={detailsL('website', L)} aud={isOwner ? shown.contact.website.aud : null} href={shown.contact.website.value} /> : null}
                  {LINK_KEYS.map((k) => {
                    const v = shown.contact.links[k];
                    return v ? <Row key={k} logo={<Badge icon="link" />} title={linkHandle(v.value)} sub={LINK_NAME[k]} aud={isOwner ? v.aud : null} href={linkUrl(k, v.value)} /> : null;
                  })}
                </>
              )}
            </Sec>
          )}
        </div>

        {/* 🟥 Only the top view used to be drawn, so the company / school search
            UNMOUNTED the form under it and a pick landed nowhere ("I select Google
            and it does not select"). Every view on the stack stays mounted now. */}
        {stack.map((r, i) => {
          const key = `${i}_${r.kind}`;
          if (r.kind === 'work') {
            return (
              <WorkForm key={key} item={r.item} push={push} onCancel={pop} isNew={!draft.work.some((x) => x.id === r.item.id)}
                onDone={(w) => { change((d) => { const j = d.work.findIndex((x) => x.id === w.id); if (j >= 0) d.work[j] = w; else d.work.push(w); }); pop(); }}
                onRemove={() => { change((d) => { d.work = d.work.filter((x) => x.id !== r.item.id); }); pop(); }} />
            );
          }
          if (r.kind === 'edu') {
            return (
              <EduForm key={key} item={r.item} push={push} onCancel={pop} isNew={!draft.education.some((x) => x.id === r.item.id)}
                onDone={(e) => { change((d) => { const j = d.education.findIndex((x) => x.id === e.id); if (j >= 0) d.education[j] = e; else d.education.push(e); }); pop(); }}
                onRemove={() => { change((d) => { d.education = d.education.filter((x) => x.id !== r.item.id); }); pop(); }} />
            );
          }
          if (r.kind === 'search') return <SearchView key={key} mode={r.mode} onBack={pop} onPick={(v) => { r.onPick(v); pop(); }} />;
          if (r.kind === 'lang') {
            return (
              <LangView key={key} have={draft.basic.languages ? draft.basic.languages.value : []} onBack={pop}
                onPick={(l) => { change((d) => { const cur = d.basic.languages ? d.basic.languages.value : []; if (!cur.includes(l)) d.basic.languages = { value: cur.concat([l]), aud: d.basic.languages ? d.basic.languages.aud : 'public' }; }); pop(); }} />
            );
          }
          return null;
        })}
        {savedFlash ? <div className="pd-saved" role="status"><Check width={15} height={15} />{detailsL('saved', L)}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

function Sec({ title, children }) { return <section className="pd-sec"><h3>{title}</h3>{children}</section>; }
function Row({ logo, title, sub, aud, onClick, href }) {
  const inner = (
    <>
      {logo}
      <span className="pd-txt"><b>{title}</b>{sub ? <i>{sub}</i> : null}</span>
      {aud ? <AudIcon a={aud} /> : null}
      {onClick ? <ChevronRight width={16} height={16} /> : null}
    </>
  );
  if (href) return <a className="pd-item" href={href} target="_blank" rel="noreferrer noopener">{inner}</a>;
  if (onClick) return <button type="button" className="pd-item" onClick={onClick}>{inner}</button>;
  return <div className="pd-item">{inner}</div>;
}
function AddRow({ label, onClick }) {
  return <button type="button" className="pd-item add" onClick={onClick}><span className="pd-logo blank"><Plus width={18} height={18} /></span><span className="pd-txt"><b>{label}</b></span></button>;
}
function Chips({ items, value, onChange, label }) {
  return (
    <div className="pd-chips">
      {items.map((it) => <button type="button" key={it} className={`pd-chip ${value === it ? 'on' : ''}`} onClick={() => onChange(value === it ? undefined : it)}>{label(it)}</button>)}
    </div>
  );
}
function LinkEdit({ label, ph, value, aud, onChange, onAud }) {
  return (
    <div className="pd-field">
      <label>{label}</label>
      <div className="pd-pick-row">
        <input className="pd-in" value={value} maxLength={200} placeholder={ph} onChange={(e) => onChange(e.target.value)} autoComplete="off" />
        {value ? <AudPill value={aud} onChange={onAud} /> : null}
      </div>
    </div>
  );
}
function Overlay({ title, onBack, right, children }) {
  return (
    <div className="pd-over">
      <header className="pd-head">
        <button type="button" className="pd-x" onClick={onBack} aria-label="Back"><X width={18} height={18} /></button>
        <div className="pd-ttl"><b>{title}</b></div>
        {right}
      </header>
      <div className="pd-body">{children}</div>
    </div>
  );
}
function Years({ item, set, currentLabel }) {
  const L = lang();
  const years = useMemo(() => { const out = []; for (let y = maxYear(Date.now()); y >= MIN_YEAR; y -= 1) out.push(y); return out; }, []);
  return (
    <>
      <label className="pd-check"><input type="checkbox" checked={!!item.current} onChange={(e) => set({ current: e.target.checked, to: e.target.checked ? undefined : item.to })} />{currentLabel}</label>
      <div className="pd-two">
        <div className="pd-field"><label>{detailsL('from_year', L)}</label>
          <select className="pd-in" value={item.from || ''} onChange={(e) => set({ from: e.target.value ? Number(e.target.value) : undefined })}><option value="">—</option>{years.map((y) => <option key={y} value={y}>{y}</option>)}</select>
        </div>
        <div className="pd-field"><label>{detailsL('to_year', L)}</label>
          {item.current ? <div className="pd-in muted">{detailsL('present', L)}</div> : (
            <select className="pd-in" value={item.to || ''} onChange={(e) => set({ to: e.target.value ? Number(e.target.value) : undefined })}><option value="">—</option>{years.map((y) => <option key={y} value={y}>{y}</option>)}</select>
          )}
        </div>
      </div>
    </>
  );
}
function AudChoice({ value, onChange }) {
  const L = lang();
  return (
    <div className="pd-field">
      <label>{detailsL('who_sees', L)}</label>
      <div className="pd-seg">
        {AUDIENCES.map((a) => { const I = AUD_ICON[a]; return <button type="button" key={a} className={value === a ? 'on' : ''} onClick={() => onChange(a)}><I width={14} height={14} />{detailsL(a, L)}</button>; })}
      </div>
    </div>
  );
}
function WorkForm({ item, onDone, onCancel, onRemove, isNew, push }) {
  const L = lang();
  const [w, setW] = useState(item);
  const set = (p) => setW((cur) => ({ ...cur, ...p }));
  return (
    <Overlay title={detailsL('work', L)} onBack={onCancel} right={<button type="button" className="pd-btn primary" disabled={!w.org.name} onClick={() => onDone(w)}>{detailsL('done', L)}</button>}>
      <div className="pd-field"><label>{detailsL('company', L)}</label>
        <button type="button" className="pd-pick" onClick={() => push({ kind: 'search', mode: 'company', onPick: (o) => set({ org: o }) })}>
          {w.org.name ? <><OrgLogo org={w.org} size={26} />{w.org.name}</> : <span className="ph"><Search width={14} height={14} />{detailsL('search_company', L)}</span>}
        </button>
      </div>
      <div className="pd-field"><label>{detailsL('position', L)}</label><input className="pd-in" maxLength={100} value={w.title || ''} onChange={(e) => set({ title: e.target.value })} /></div>
      <div className="pd-field"><label>{detailsL('city_town', L)}</label>
        <button type="button" className="pd-pick" onClick={() => push({ kind: 'search', mode: 'city', onPick: (p) => set({ city: placeLabel(p) }) })}>{w.city || <span className="ph"><Search width={14} height={14} />{detailsL('search_city', L)}</span>}</button>
      </div>
      <Years item={w} set={set} currentLabel={detailsL('current_work', L)} />
      <AudChoice value={w.aud} onChange={(a) => set({ aud: a })} />
      {!isNew ? <button type="button" className="pd-btn danger wide" onClick={onRemove}><Trash width={14} height={14} />{detailsL('remove', L)}</button> : null}
    </Overlay>
  );
}
function EduForm({ item, onDone, onCancel, onRemove, isNew, push }) {
  const L = lang();
  const [e, setE] = useState(item);
  const set = (p) => setE((cur) => ({ ...cur, ...p }));
  return (
    <Overlay title={detailsL('education', L)} onBack={onCancel} right={<button type="button" className="pd-btn primary" disabled={!((e.org && e.org.name) || e.degree)} onClick={() => onDone(e)}>{detailsL('done', L)}</button>}>
      <div className="pd-field"><label>{detailsL('school_type', L)}</label>
        <div className="pd-seg">{SCHOOL_KINDS.map((k) => <button type="button" key={k} className={e.kind === k ? 'on' : ''} onClick={() => set({ kind: k, ...(k === 'high_school' ? { degree: undefined } : {}) })}>{schoolKindLabel(k, L)}</button>)}</div>
      </div>
      <div className="pd-field"><label>{detailsL('school', L)}</label>
        <button type="button" className="pd-pick" onClick={() => push({ kind: 'search', mode: 'school', onPick: (o) => set({ org: o }) })}>
          {e.org && e.org.name ? <><OrgLogo org={e.org} size={26} icon="school" />{e.org.name}</> : <span className="ph"><Search width={14} height={14} />{detailsL('search_school', L)}</span>}
        </button>
      </div>
      {e.kind !== 'high_school' ? (
        <>
          <div className="pd-field"><label>{detailsL('degree', L)}</label><Chips items={DEGREES} value={e.degree} onChange={(v) => set({ degree: v })} label={(d) => degreeLabel(d, L)} /></div>
          <div className="pd-field"><label>{detailsL('field', L)}</label><input className="pd-in" maxLength={100} value={e.field || ''} onChange={(ev) => set({ field: ev.target.value })} /></div>
        </>
      ) : null}
      <Years item={e} set={set} currentLabel={detailsL('current_school', L)} />
      <AudChoice value={e.aud} onChange={(a) => set({ aud: a })} />
      {!isNew ? <button type="button" className="pd-btn danger wide" onClick={onRemove}><Trash width={14} height={14} />{detailsL('remove', L)}</button> : null}
    </Overlay>
  );
}
function SearchView({ mode, onPick, onBack }) {
  const L = lang();
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [hits, setHits] = useState([]);
  const seq = useRef(0);
  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setHits([]); setBusy(false); return undefined; }
    const mine = ++seq.current;
    setBusy(true);
    const tm = setTimeout(async () => {
      const r = mode === 'city' ? await searchPlaces(t) : await searchOrgs(mode, t);
      if (mine !== seq.current) return;
      setHits(r); setBusy(false);
    }, 300);
    return () => clearTimeout(tm);
  }, [q, mode]);
  const t = q.trim();
  const exact = hits.some((h) => String(h.name || '').toLowerCase() === t.toLowerCase());
  const ph = detailsL(mode === 'city' ? 'search_city' : mode === 'school' ? 'search_school' : 'search_company', L);
  return (
    <Overlay title={ph} onBack={onBack}>
      <div className="pd-search"><Search width={16} height={16} /><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={ph} />{busy ? <span className="pd-spin" /> : null}</div>
      {t.length >= 2 && !exact && mode !== 'city' ? (
        <button type="button" className="pd-item" onClick={() => onPick({ name: t })}><OrgLogo org={{ name: t }} /><span className="pd-txt"><b>{fill(detailsL('use_typed', L), { q: t })}</b></span></button>
      ) : null}
      {hits.map((h, i) => (
        <button type="button" key={`${h.qid || h.name}_${i}`} className="pd-item" onClick={() => onPick(mode === 'city' ? h : { name: h.name, qid: h.qid, domain: h.domain, logo: h.logo })}>
          {mode === 'city' ? <Badge icon="location" flag={flagOf(h.cc)} /> : <OrgLogo org={h} icon={mode === 'school' ? 'school' : 'briefcase'} />}
          <span className="pd-txt"><b>{h.name}</b><i>{mode === 'city' ? [h.region, h.country].filter(Boolean).join(', ') : (h.desc || h.place || h.domain || '')}</i></span>
        </button>
      ))}
      {!busy && t.length >= 2 && !hits.length && mode === 'city' ? <p className="pd-note center">{detailsL('no_results', L)}</p> : null}
    </Overlay>
  );
}
function LangView({ have, onPick, onBack }) {
  const L = lang();
  const [q, setQ] = useState('');
  const t = q.trim();
  const list = LANGUAGES.filter((l) => !have.includes(l) && l.toLowerCase().includes(t.toLowerCase()));
  return (
    <Overlay title={detailsL('add_language', L)} onBack={onBack}>
      <div className="pd-search"><Languages width={16} height={16} /><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={detailsL('add_language', L)} /></div>
      {t && !LANGUAGES.some((l) => l.toLowerCase() === t.toLowerCase()) ? <button type="button" className="pd-item" onClick={() => onPick(t)}><span className="pd-txt"><b>{fill(detailsL('use_typed', L), { q: t })}</b></span></button> : null}
      {list.map((l) => <button type="button" key={l} className="pd-item" onClick={() => onPick(l)}><span className="pd-txt"><b>{l}</b></span></button>)}
    </Overlay>
  );
}
